import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, ChefHat, Bath, Home, Warehouse, Lightbulb } from 'lucide-react';
import { Button } from './ui/button';

const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [activeService, setActiveService] = useState(0);

  const services = [
    {
      icon: ChefHat,
      title: 'Kitchen Remodeling',
      description:
        'Transform your kitchen into a stunning culinary space with custom cabinetry, premium countertops, and modern appliances. We focus on functionality and aesthetics.',
      image:
        'https://images.unsplash.com/photo-1683629357884-11440746a7ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtb2Rlcm4lMjBraXRjaGVuJTIwcmVub3ZhdGlvbiUyMHdoaXRlJTIwZ29sZCUyMG1hcmJsZXxlbnwwfHx8fDE3NzI3ODI4MDV8MA&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: Bath,
      title: 'Bathroom Remodeling',
      description:
        'Create a spa-like retreat with elegant fixtures, custom tile work, and luxurious finishes. From powder rooms to master baths, we deliver exceptional results.',
      image:
        'https://images.unsplash.com/photo-1772567732962-9c9a4dfff8d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtb2Rlcm4lMjBiYXRocm9vbSUyMHJlbm92YXRpb24lMjBzcGElMjBsaWtlfGVufDB8fHx8MTc3Mjc4MjgwNnww&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: Home,
      title: 'Full Home Renovations',
      description:
        'Complete home transformations that reimagine your living space. We handle everything from structural changes to finishing touches with expert precision.',
      image:
        'https://images.unsplash.com/photo-1758548157747-285c7012db5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBsaXZpbmclMjByb29tJTIwaG9tZSUyMHJlbm92YXRpb24lMjBicmlnaHQlMjBlbGVnYW50fGVufDB8fHx8MTc3Mjc4MjgwN3ww&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: Warehouse,
      title: 'Garage Renovations',
      description:
        'Transform your garage into a functional, organized space. Whether you need a workshop, storage solution, or modernized parking area, we deliver quality results.',
      image:
        'https://images.unsplash.com/photo-1765987714974-cd7dd4357f39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBnYXJhZ2UlMjBpbnRlcmlvciUyMHJlbm92YXRpb24lMjBjbGVhbiUyMHN0b3JhZ2V8ZW58MHx8fHwxNzcyNzgyODA3fDA&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: Lightbulb,
      title: 'Electrical & Interior',
      description:
        'Professional electrical installations and interior improvements. Upgrade your lighting, outlets, and interior features for improved safety and aesthetics.',
      image:
        'https://images.unsplash.com/photo-1630699144035-c0f6311ec482?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBsaXZpbmclMjByb29tJTIwaG9tZSUyMHJlbm92YXRpb24lMjBicmlnaHQlMjBlbGVnYW50fGVufDB8fHx8MTc3Mjc4MjgwN3ww&ixlib=rb-4.1.0&q=85',
    },
  ];

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="services"
      data-testid="services-section"
      ref={ref}
      className="section-padding bg-[#F4F4F5]"
    >
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="text-[#D4AF37] text-sm font-medium tracking-[0.2em] uppercase mb-4">
            Our Services
          </p>
          <h2
            data-testid="services-title"
            className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#0A0A0A] font-bold mb-6"
          >
            Renovation Excellence
          </h2>
          <div className="w-16 h-0.5 bg-[#D4AF37] mx-auto" />
        </motion.div>

        {/* Services Layout */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Service List */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-2"
          >
            {services.map((service, index) => (
              <div
                key={service.title}
                data-testid={`service-item-${index}`}
                onClick={() => setActiveService(index)}
                className={`group cursor-pointer p-6 transition-all duration-300 ${
                  activeService === index
                    ? 'bg-white shadow-lg border-l-4 border-[#D4AF37]'
                    : 'bg-transparent hover:bg-white/50 border-l-4 border-transparent'
                }`}
              >
                <div className="flex items-start gap-4">
                  <service.icon
                    className={`w-6 h-6 flex-shrink-0 transition-colors ${
                      activeService === index
                        ? 'text-[#D4AF37]'
                        : 'text-[#71717A] group-hover:text-[#D4AF37]'
                    }`}
                  />
                  <div className="flex-1">
                    <h3
                      className={`font-serif text-xl font-semibold mb-2 transition-colors ${
                        activeService === index
                          ? 'text-[#0A0A0A]'
                          : 'text-[#27272A]'
                      }`}
                    >
                      {service.title}
                    </h3>
                    <p
                      className={`text-sm leading-relaxed transition-all duration-300 ${
                        activeService === index
                          ? 'text-[#71717A] max-h-40 opacity-100'
                          : 'max-h-0 opacity-0 overflow-hidden'
                      }`}
                    >
                      {service.description}
                    </p>
                  </div>
                  <ArrowRight
                    className={`w-5 h-5 flex-shrink-0 transition-all ${
                      activeService === index
                        ? 'text-[#D4AF37] translate-x-0'
                        : 'text-[#E4E4E7] -translate-x-2 group-hover:translate-x-0 group-hover:text-[#D4AF37]'
                    }`}
                  />
                </div>
              </div>
            ))}
          </motion.div>

          {/* Service Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <div className="sticky top-32">
              <div className="relative aspect-[4/3] overflow-hidden bg-[#0A0A0A]">
                {services.map((service, index) => (
                  <img
                    key={service.title}
                    src={service.image}
                    alt={service.title}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                      activeService === index ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                ))}
                {/* Overlay with service name */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-[#D4AF37] text-sm tracking-widest uppercase mb-2">
                    Featured Service
                  </p>
                  <h3 className="font-serif text-2xl text-white font-semibold">
                    {services[activeService].title}
                  </h3>
                </div>
              </div>
              <Button
                onClick={scrollToContact}
                data-testid="services-cta"
                className="w-full mt-4 bg-[#0A0A0A] text-white hover:bg-[#27272A] rounded-none py-6 text-base"
              >
                Request a Quote for This Service
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Services;
